import { AccessStatus, type PersonalVaultAdministration } from '@/modules/api/codegen';

import type { VaultsTableFilters, VaultsTablePagination, VaultsTableRow, VaultsTableSort } from '../types';

export const isPersonalVaultRow = (row: VaultsTableRow): row is PersonalVaultAdministration => 'owner' in row;

// The endpoint deletes a personal vault only once its owner is offboarded; `null` is an owner the
// directory no longer knows at all.
export const isPersonalVaultDeletable = ({ ownerStatus }: PersonalVaultAdministration) =>
    ownerStatus === AccessStatus.inactive || ownerStatus === null;

export const personalVaultOwnerLabel = ({ owner }: PersonalVaultAdministration) => owner.name || owner.email;

// Mirrors what the server does to the team list for the same parameters.
const matchesFilters =
    ({ search, users, groups }: VaultsTableFilters) =>
    (vault: PersonalVaultAdministration) => {
        if (groups) {
            return false;
        }

        if (users && !users.split(',').includes(vault.owner.id)) {
            return false;
        }

        return !search || vault.name.toLowerCase().includes(search.toLowerCase());
    };

const compareByName = (a: PersonalVaultAdministration, b: PersonalVaultAdministration) =>
    a.name.localeCompare(b.name) || personalVaultOwnerLabel(a).localeCompare(personalVaultOwnerLabel(b));

export interface PersonalVaultsPageOptions {
    filters: VaultsTableFilters;
    sort: VaultsTableSort;
    pagination: Required<VaultsTablePagination>;
    teamTotal: number;
}

/**
 * The server pages the team list and the administration list is not paged at all, while the design
 * shows both as one list with every personal vault after the last team vault. So the merged sequence is
 * paged as a whole: a page holds the team vaults the server returned for it, followed by the personal
 * vaults whose position in that sequence falls inside the page.
 */
export const selectPersonalVaultsPage = (
    vaults: PersonalVaultAdministration[],
    { filters, sort, pagination, teamTotal }: PersonalVaultsPageOptions,
) => {
    const matching = vaults.filter(matchesFilters(filters)).sort(compareByName);

    if (sort.orderDirection === 'desc') {
        matching.reverse();
    }

    const start = (pagination.page - 1) * pagination.limit;
    const end = start + pagination.limit;

    return {
        rows: matching.slice(Math.max(0, start - teamTotal), Math.max(0, end - teamTotal)),
        total: matching.length,
    };
};
